// Mapping between article text offsets and the DOM, and painting highlights.
//
// Highlights can't be rendered through a virtual DOM: the article is arbitrary
// third-party HTML, and a highlight frequently spans element boundaries (half
// of one <em>, across a <p>, into the next). So this is deliberately
// imperative DOM surgery — the part of the reader a component framework would
// not have helped with.
import { normalizeText } from './anchor.ts';

/** Where one normalized-text character came from in the DOM. */
export interface CharPosition {
  node: Text;
  offset: number;
}

export interface Flattened {
  text: string;
  /** positions[i] is the DOM origin of normalized character i. */
  positions: CharPosition[];
}

/** A run of characters inside a single text node. */
/** What painting reads from an annotation; extra fields are ignored. */
export interface PaintableAnnotation {
  id: number;
  comment?: string | null;
  color?: string | null;
}

/** A resolved location — `locate()` minus the orphan case. */
export interface Anchored {
  start: number;
  end: number;
  method: string;
}

/** A run of characters inside a single text node. */
export interface Segment {
  node: Text;
  start: number;
  end: number;
}

/**
 * Flatten a root element's text, keeping a map back to the DOM.
 *
 * Returns `{text, positions}` where `text` is whitespace-normalized (so
 * matching ignores HTML indentation) and `positions[i]` is the
 * `{node, offset}` that normalized character `i` came from. Storing a position
 * per character is O(n) memory — a few hundred KB for a long article, which is
 * cheap next to the article HTML itself, and it makes offset→Range exact
 * rather than approximate.
 */
export function flatten(root: HTMLElement): Flattened {
  const walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let text = '';
  const positions: CharPosition[] = [];
  let lastWasSpace = true; // leading whitespace is dropped, as in normalizeText

  for (let node = walker.nextNode() as Text | null; node; node = walker.nextNode() as Text | null) {
    // Skip text inside our own UI — a comment chip's text is not article text.
    if (node.parentElement?.closest('[data-annotation-ui]')) continue;

    const value = node.nodeValue || '';
    for (let i = 0; i < value.length; i++) {
      // The loop bound guarantees the index is in range.
      const char = value[i]!;
      const isSpace = /\s/.test(char);
      if (isSpace) {
        if (lastWasSpace) continue; // collapse runs
        text += ' ';
        positions.push({ node, offset: i });
        lastWasSpace = true;
      } else {
        text += char;
        positions.push({ node, offset: i });
        lastWasSpace = false;
      }
    }
  }

  return { text, positions };
}

/**
 * Turn a normalized-text span into the text-node segments it covers.
 * A span crossing element boundaries yields one segment per text node.
 */
export function segmentsFor(positions: CharPosition[], start: number, end: number): Segment[] {
  const segments: Segment[] = []; 
  let current: Segment | null = null;

  for (let i = start; i < end && i < positions.length; i++) {
    const { node, offset } = positions[i]!;
    if (current && current.node === node && offset === current.end) {
      current.end = offset + 1;
      continue;
    }
    if (current) segments.push(current);
    current = { node, start: offset, end: offset + 1 };
  }

  if (current) segments.push(current);
  return segments;
}

/**
 * Wrap text-node segments in elements built by `makeWrapper`.
 *
 * Segments are applied per node, latest-first: splitText invalidates offsets
 * after the split point, so working backwards keeps the remaining offsets
 * valid without re-flattening between each one.
 */
export function wrapSegments(segments: Segment[], makeWrapper: () => HTMLElement): HTMLElement[] {
  const byNode = new Map<Text, Segment[]>();
  for (const segment of segments) {
    if (!byNode.has(segment.node)) byNode.set(segment.node, []);
    byNode.get(segment.node)!.push(segment);
  }

  const wrappers: HTMLElement[] = [];

  for (const [node, nodeSegments] of byNode) {
    nodeSegments.sort((a, b) => b.start - a.start);

    let lastStart = Infinity;
    for (const segment of nodeSegments) {
      // Overlapping highlights on the same text run would corrupt each other's
      // offsets, so a later segment is clipped to what's still free.
      const end = Math.min(segment.end, lastStart);
      if (end <= segment.start) continue;

      let target = node;
      if (segment.start > 0) target = target.splitText(segment.start);
      if (end - segment.start < target.nodeValue!.length) {
        target.splitText(end - segment.start);
      }

      const wrapper = makeWrapper();
      target.parentNode!.replaceChild(wrapper, target);
      wrapper.appendChild(target);
      wrappers.push(wrapper);

      lastStart = segment.start;
    }
  }

  return wrappers;
}

/**
 * Wrap each located range in elements, and return the wrappers by index (an
 * empty list for a `null` range, or one that couldn't be wrapped).
 *
 * Ranges are wrapped highest-start first, whatever order they are listed in.
 * Wrapping splits text nodes, which invalidates every offset after the split
 * point, so an offset is only still good if everything after it was wrapped
 * before it. Ranges found by searching for each sentence in turn are *not*
 * guaranteed to be in article order — a repeated phrase resolves to whichever
 * occurrence is nearest, which can be behind the previous match — and wrapping
 * them in list order split a node at a low offset and then asked for a higher
 * one past its new end, throwing and losing every sentence marker.
 */
export function wrapRanges(
  positions: CharPosition[],
  ranges: Array<{ start: number; end: number } | null>,
  makeWrapper: (index: number) => HTMLElement,
): HTMLElement[][] {
  const wrapped: HTMLElement[][] = ranges.map(() => []);

  const order = ranges
    .map((range, index) => ({ range, index }))
    .filter((entry): entry is { range: { start: number; end: number }; index: number } => entry.range !== null)
    .sort((a, b) => b.range.start - a.range.start || b.range.end - a.range.end);

  for (const { range, index } of order) {
    try {
      wrapped[index] = wrapSegments(segmentsFor(positions, range.start, range.end), () => makeWrapper(index));
    } catch {
      // A range that can't be wrapped (its text was consumed by a longer one
      // painted first) loses its own marker, not everyone else's.
    }
  }
  return wrapped;
}

/** Remove highlight wrappers, restoring the original text nodes. */
export function clearHighlights(root: HTMLElement): void {
  for (const mark of [...root.querySelectorAll('[data-annotation-id]')]) {
    const parent = mark.parentNode!;
    while (mark.firstChild) parent.insertBefore(mark.firstChild, mark);
    parent.removeChild(mark);
  }
  for (const ui of [...root.querySelectorAll('[data-annotation-ui]')]) {
    ui.remove();
  }
  // splitText leaves the article fragmented; without this, repeated paint
  // cycles would split text nodes into ever-smaller pieces.
  root.normalize();
}

/**
 * Where a DOM range falls in the root's normalized text: the part of the range
 * that lies inside `root`, as `{start, end}`. A range reaching past either edge
 * of `root` is clipped to it — the PDF reader relies on that, since a drag from
 * one page's text layer into the next has to become a highlight on the page
 * it started on. Returns null when nothing of the range is inside `root`.
 *
 * Boundaries may be text nodes (a drag) or elements (a triple-click, or
 * select-all); either way they are resolved against the text nodes in `root`.
 */
export function rangeOffsets(
  root: HTMLElement,
  range: Range,
): { start: number; end: number } | null {
  if (range.collapsed) return null;

  const doc = root.ownerDocument;
  const texts: Text[] = [];
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  for (let n = walker.nextNode() as Text | null; n; n = walker.nextNode() as Text | null) texts.push(n);

  const startPoint = doc.createRange();
  startPoint.setStart(range.startContainer, range.startOffset);
  startPoint.collapse(true);
  const endPoint = doc.createRange();
  endPoint.setStart(range.endContainer, range.endOffset);
  endPoint.collapse(true);

  // The first text node with any text after the start boundary, and the last
  // with any text before the end boundary.
  const startIndex = texts.findIndex((t) => startPoint.comparePoint(t, t.length) > 0);
  let endIndex = -1;
  for (let i = texts.length - 1; i >= 0; i--) {
    if (endPoint.comparePoint(texts[i]!, 0) < 0) {
      endIndex = i;
      break;
    }
  }
  if (startIndex === -1 || endIndex === -1 || endIndex < startIndex) return null;

  const startNode = texts[startIndex]!;
  const endNode = texts[endIndex]!;
  const startOffset = startNode === range.startContainer ? range.startOffset : 0;
  const endOffset = endNode === range.endContainer ? range.endOffset : endNode.length;
  const order = new Map(texts.map((t, i) => [t, i]));

  // Compared by node order rather than by identity, so a boundary node with no
  // characters of its own (whitespace-only between paragraphs, say) doesn't
  // lose the selection.
  const { positions } = flatten(root);
  let start = -1;
  let end = -1;
  for (let i = 0; i < positions.length; i++) {
    const { node, offset } = positions[i]!;
    const index = order.get(node)!;
    if (start === -1 && (index > startIndex || (index === startIndex && offset >= startOffset))) start = i;
    if (index < endIndex || (index === endIndex && offset < endOffset)) end = i + 1;
  }

  if (start === -1 || end === -1 || end <= start) return null;
  return { start, end };
}

/**
 * Where a DOM selection falls in the normalized article text.
 * Returns `{start, end}` or null when the selection isn't inside the article.
 */
export function selectionOffsets(
  root: HTMLElement,
  selection: Selection | null,
): { start: number; end: number } | null {
  if (!selection || selection.isCollapsed || selection.rangeCount === 0) return null;

  const range = selection.getRangeAt(0);
  if (!root.contains(range.commonAncestorContainer)) return null;

  return rangeOffsets(root, range);
}

/** Paint one located annotation. Returns the wrapper elements it created. */
export function paintAnnotation(
  root: HTMLElement,
  positions: CharPosition[],
  annotation: PaintableAnnotation,
  located: Anchored,
): HTMLElement[] {
  const segments = segmentsFor(positions, located.start, located.end);
  if (!segments.length) return [];

  return wrapSegments(segments, () => {
    const mark = root.ownerDocument.createElement('mark');
    mark.className = `annotation${annotation.comment ? ' has-comment' : ''}`;
    mark.dataset.annotationId = String(annotation.id);
    if (annotation.color) mark.dataset.color = annotation.color;
    if (located.method !== 'exact') mark.dataset.method = located.method;
    mark.title = annotation.comment || '';
    return mark;
  });
}

export { normalizeText };
